import React from 'react';

import { ATTRIBUTE_LIMIT } from '@neptune/shared/attributes-domain';
import { useMemoizedValue } from '@neptune/shared/common-util';
import { FilterResult, SEARCH_MODES, SearchMode } from '@neptune/shared/search-domain';

import { AttributeDefinition } from 'domain/experiment/attribute';

import {
  type AttributeDefinitionPrioritizedDataSourceParams,
  createAttributeDefinitionPrioritizedDataSource,
} from './use-attribute-definition-prioritized-data-source';

export type ScopedAttributeDefinitionPrioritizedResult<
  T extends AttributeDefinition = AttributeDefinition,
> = (
  query: string,
  searchMode?: SearchMode,
) => Promise<FilterResult<T> & { priorityMatches: Map<string, boolean> }>;

export function useScopedAttributeDefinitionPrioritizedDataSource<
  T extends AttributeDefinition = AttributeDefinition,
>(
  params: AttributeDefinitionPrioritizedDataSourceParams<T>,
): ScopedAttributeDefinitionPrioritizedResult<T> {
  const limit = params.limit ?? ATTRIBUTE_LIMIT;

  const scopedParams = useMemoizedValue({ ...params, limit });

  return React.useCallback(
    async (query: string, searchMode?: SearchMode) => {
      if (searchMode === SEARCH_MODES.FULL || scopedParams.fullSearch) {
        const result = await createAttributeDefinitionPrioritizedDataSource({
          ...scopedParams,
          fullSearch: true,
        })(query);

        return {
          ...result,
          searchMode: SEARCH_MODES.FULL,
        };
      }

      const result = await createAttributeDefinitionPrioritizedDataSource(scopedParams)(query);

      return {
        ...result,
        searchMode: SEARCH_MODES.SCOPED,
        needsFullSearch: result.entries.length < scopedParams.limit,
      };
    },
    [scopedParams],
  );
}
